import { getOffer, getOfferFinanceConditions, getTeslaTrimCodes, updateOffer } from 'api/offers';
import { SOMETHING_WENT_WRONG } from 'constants/errors';
import moment from 'moment';
import { getErrorMessages } from 'pages/AddEditListings/utils';
import GlobalSelectors from 'pages/_store/global/selectors';
import { fork, put, takeEvery } from 'redux-saga/effects';
import { call, select } from 'typed-redux-saga';
import { LeasingMileage, LeasingPeriod, PublicOfferPricing, UpdateOrderQuery } from 'types';
import { YYYY_MM_DD } from 'utils/date';
import { Actions as OrdersActions, ActionTypes as OrdersActionTypes } from './actions';

function* getOrdersRequestAsync(action: ReturnType<typeof OrdersActions.getOrderRequest>) {
  try {
    window.scrollTo(0, 0);
    const res = yield* call(getOffer, action.payload);

    if (res.esDeal.licensingDateRaw) {
      res.esDeal.licensingDateRaw = moment(res.esDeal.licensingDateRaw).format(YYYY_MM_DD);
    }

    const nominalInterestRateOriginal = res.nominalInterestRateOriginal || res.interestRateNominalConfirmed;

    yield put(
      OrdersActions.getOrderSuccess({
        ...res,
        nationality: res.insuranceNationality,
        ageCategory: res.insuranceAgeCategory,
        nominalInterestRateOriginal,
      })
    );
  } catch (e) {
    yield put(OrdersActions.getOrderFailure(e));
  }
}

function* getOrdersFinanceConditionsRequestAsync(
  action: ReturnType<typeof OrdersActions.getOrderFinanceConditionsRequest>
) {
  try {
    window.scrollTo(0, 0);
    const res = yield* call(getOfferFinanceConditions, action.payload);

    yield put(OrdersActions.getOrderFinanceConditionsSuccess(res));
  } catch (e) {
    yield put(OrdersActions.getOrderFinanceConditionsFailure(e));
  }
}

function handleResidualValueOverride(
  orderData: PublicOfferPricing,
  specialResidualValueOverride: UpdateOrderQuery['data']['specialResidualValueOverride'],
  leasingMileage: LeasingMileage,
  leasingPeriod: LeasingPeriod
): void {
  if (specialResidualValueOverride?.overrideReason?.length) {
    const { overridingResidualValues } = specialResidualValueOverride;
    const firstResidualValue = overridingResidualValues?.[0];

    if (firstResidualValue) {
      orderData.specialResidualValueOverride = {
        ...specialResidualValueOverride,
        overridingResidualValues: [
          {
            km: leasingMileage,
            month: leasingPeriod,
            residualValueAbsolute: firstResidualValue.residualValueAbsolute,
          },
        ],
      };
    }
  } else {
    orderData.specialResidualValueOverride = null;
  }
}

function* updateOrderRequestAsync(action: ReturnType<typeof OrdersActions.updateOrderRequest>) {
  try {
    const isSuperAdmin = yield select(GlobalSelectors.getIsSuperAdmin);
    const isResidualValueAdmin = yield select(GlobalSelectors.getIsResidualValueAdmin);
    const isOfferPricingUpdateAdmin = yield select(GlobalSelectors.getIsOfferPricingUpdateAdmin);

    const isSuperAdminOrPricingUpdateAdmin = isSuperAdmin || isOfferPricingUpdateAdmin;

    const {
      data: { specialResidualValueOverride, leasingMileage, leasingPeriod },
    } = action.payload as UpdateOrderQuery;

    if (isResidualValueAdmin) {
      const orderData = action.payload.data as PublicOfferPricing;
      handleResidualValueOverride(orderData, specialResidualValueOverride, leasingMileage, leasingPeriod);
    }

    yield* call(updateOffer, { ...action.payload }, isSuperAdminOrPricingUpdateAdmin, isResidualValueAdmin);

    window.scrollTo(0, 0);
    yield put(OrdersActions.updateOrderSuccess());
    yield put(OrdersActions.getOrderRequest(action.payload.publicId));
  } catch (e) {
    window.scrollTo(0, 0);

    const errorData = e?.data;
    const errorMessage = e?.message;
    const sanityIssues = e?.sanityIssues;

    let message: string[] = [];
    if (!e) {
      message = [SOMETHING_WENT_WRONG];
    } else if (errorData || errorMessage) {
      // TODO very bad and specific case, agree with BE about validation error format and refactor this
      if (errorData?.minDownPayment || errorData?.maximumPrivateCreditAmount) {
        message = getErrorMessages(errorMessage);
      } else {
        message = getErrorMessages(errorData || errorMessage);
      }
      //
    } else if (sanityIssues) {
      message = sanityIssues;
    } else {
      message = Object.entries(e).map(([key, value]) => JSON.stringify({ [key]: value }, undefined, 2));
    }

    yield put(OrdersActions.updateOrderFailure(message));
  }
}

function* getTeslaTrimCodesRequestAsync(action: ReturnType<typeof OrdersActions.getTeslaTrimCodesRequest>) {
  try {
    const res = yield* call(getTeslaTrimCodes, action.payload.publicId, action.payload.modelSlug);

    yield put(OrdersActions.getTeslaTrimCodesSuccess(res));
  } catch (e) {
    yield put(OrdersActions.getTeslaTrimCodesFailure(e));
  }
}

function* watchGetOrdersRequestAsync() {
  yield takeEvery(OrdersActionTypes.GET_ORDER_REQUEST, getOrdersRequestAsync);
}

function* watchUpdateOrderRequestAsync() {
  yield takeEvery(OrdersActionTypes.UPDATE_ORDER_REQUEST, updateOrderRequestAsync);
}

function* watchGetTeslaTrimCodesRequestAsync() {
  yield takeEvery(OrdersActionTypes.GET_TESLA_TRIM_CODES_REQUEST, getTeslaTrimCodesRequestAsync);
}

function* watchGetOrdersFinanceConditionsRequestAsync() {
  yield takeEvery(OrdersActionTypes.GET_ORDER_FINANCE_CONDITIONS_REQUEST, getOrdersFinanceConditionsRequestAsync);
}

export default [
  fork(watchGetOrdersRequestAsync),
  fork(watchUpdateOrderRequestAsync),
  fork(watchGetTeslaTrimCodesRequestAsync),
  fork(watchGetOrdersFinanceConditionsRequestAsync),
];
